import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Music, Clock, AlertCircle, Flag, CirclePlus, Volume2, VolumeX, Banknote } from 'lucide-react';
import { useAdminNotifications } from '../../Context/NotificationContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import './NotificationBell.scss';
import { formatNotificationCount } from '../../utils/formatNotificationCount';

// Extend dayjs with relativeTime plugin for "time ago" formatting
dayjs.extend(relativeTime);

const AdminNotificationBell = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    isConnected,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    adminPage,
    adminTotalPages,
  } = useAdminNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true); // Default to enabled
  const dropdownRef = useRef(null);
  const prevUnreadCountRef = useRef(unreadCount);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Load sound preference from localStorage on component mount
  useEffect(() => {
    const savedSoundPreference = localStorage.getItem('adminNotificationSoundEnabled');
    if (savedSoundPreference !== null) {
      setSoundEnabled(JSON.parse(savedSoundPreference));
    }
  }, []);

  // Watch for new notifications and play sound if enabled
  useEffect(() => {
    // Only play sound if unread count increased (new notification arrived)
    if (unreadCount > prevUnreadCountRef.current && soundEnabled && isConnected) {
      playNotificationSound();
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount, soundEnabled, isConnected]);

  // Function to play notification sound
  const playNotificationSound = () => {
    try {
      // Create audio context and generate a pleasant notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Create a sequence of tones for a pleasant notification sound
      const playTone = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.1, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      const now = audioContext.currentTime;
      // Play a pleasant two-tone notification sound
      playTone(800, now, 0.15);
      playTone(1000, now + 0.15, 0.15);
    } catch (error) {
      // Audio playback is optional; ignore failures
    }
  };

  // Handle sound toggle change
  const handleSoundToggle = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);
    localStorage.setItem('adminNotificationSoundEnabled', JSON.stringify(newSoundEnabled));

    // Play a test sound when enabling
    if (newSoundEnabled) {
      playNotificationSound();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && fetchNotifications) {
      fetchNotifications(); // Refresh notifications when opening
    }
  };

  // Load next page for pagination
  const handleLoadMore = async () => {
    if (!fetchNotifications) return;
    if (adminPage >= adminTotalPages) return;
    setIsLoadingMore(true);
    try {
      await fetchNotifications({ page: adminPage + 1, append: true });
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Infinite scroll inside dropdown list
  const handleScroll = event => {
    if (isLoadingMore || isLoading) return;
    if (adminPage >= adminTotalPages) return;

    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < 48) {
      handleLoadMore();
    }
  };

  // Handle notification click
  const handleNotificationClick = notification => {
    if (markAsRead && notification?._id) {
      markAsRead(notification._id);
    }

    if (notification?.notificationType === 'wallet_withdrawal_requested') {
      navigate('/dashboard/payouts');
      setIsOpen(false);
      return;
    }

    // Navigate to dashboard with the appropriate tab based on campaign status
    if (notification?.status) {
      const tabParam = notification.status;
      navigate(`/dashboard/campaigns?tab=${tabParam}`);
      setIsOpen(false); // Close the dropdown
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = () => {
    if (markAllAsRead) {
      markAllAsRead();
    }
  };

  // Get notification icon based on notification type
  const getNotificationIcon = notificationType => {
    switch (notificationType) {
      case 'campaign_created':
        return { Icon: CirclePlus, className: 'type-new-campaign' };
      case 'campaign_ending_one_day':
        return { Icon: Clock, className: 'type-warning' };
      case 'campaign_ending_five_minutes':
        return { Icon: AlertCircle, className: 'type-urgent' };
      case 'campaign_ended':
        return { Icon: Flag, className: 'type-ended' };
      case 'wallet_withdrawal_requested':
        return { Icon: Banknote, className: 'type-warning' };
      default:
        return { Icon: Music, className: 'type-music' };
    }
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <div
        className={`notification-bell ${!isConnected ? 'disconnected' : ''}`}
        onClick={toggleDropdown}
        title={isConnected ? 'Notifications' : 'Notification system disconnected'}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <div className="notification-count">{formatNotificationCount(unreadCount)}</div>
        )}
      </div>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <div className="notification-actions">
              <div className="sound-toggle-container">
                <label className="sound-toggle">
                  <input type="checkbox" checked={soundEnabled} onChange={handleSoundToggle} />
                  <span className="sound-toggle-slider"></span>
                  <span className="soundIcon">
                    {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                  </span>
                </label>
              </div>
              {unreadCount > 0 && (
                <button className="mark-all-read" onClick={handleMarkAllAsRead}>
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          <div className="notification-list" onScroll={handleScroll}>
            {error ? (
              <div className="error-message">
                {error}
                <button
                  className="retry-button"
                  onClick={() => {
                    if (fetchNotifications) {
                      fetchNotifications();
                    }
                  }}
                >
                  Retry
                </button>
              </div>
            ) : isLoading ? (
              <div className="loading">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                {isConnected
                  ? 'No admin notifications yet.'
                  : 'Unable to connect to notification server. Please refresh the page.'}
              </div>
            ) : (
              <>
                {notifications.map(notification => {
                  const { Icon: TypeIcon, className: typeIconClassName } = getNotificationIcon(
                    notification?.notificationType
                  );
                  return (
                    <div
                      key={notification?._id || Math.random().toString()}
                      className={`notification-item ${!notification?.isRead ? 'unread' : ''} ${
                        notification?.notificationType?.includes('campaign_ending')
                          ? 'campaign-ending'
                          : ''
                      } ${notification?.notificationType === 'campaign_ending_five_minutes' ? 'urgent' : ''} ${
                        notification?.notificationType === 'campaign_ended' ? 'campaign-ended' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="notification-body">
                        <div className="notification-time">
                          <span className={`notification-type-icon ${typeIconClassName}`}>
                            <TypeIcon size={12} />
                          </span>
                          {notification?.created_at
                            ? dayjs(notification.created_at).fromNow()
                            : 'Just now'}
                        </div>
                        <div className="notification-header-row">
                          <div className="notification-message">
                            {notification?.message || 'New notification'}
                          </div>
                        </div>
                        {notification?.notificationType === 'wallet_withdrawal_requested' &&
                        notification?.walletData ? (
                          <div className="notification-campaign">
                            <div className="campaign-info">
                              <span className="campaign-icon">
                                <Banknote size={16} />
                              </span>
                              <div className="campaign-details">
                                <span className="campaign-name">
                                  {notification.walletData.currencyCode}{' '}
                                  {Number(notification.walletData.amount || 0).toFixed(2)}
                                </span>
                                <span className="campaign-artist">
                                  {notification.walletData.destination}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : notification?.campaignData ? (
                          <div className="notification-campaign">
                            <div className="campaign-info">
                              <span className="campaign-icon">
                                <Music size={16} />
                              </span>
                              <div className="campaign-details">
                                <span className="campaign-name">
                                  {notification.campaignData.track_name}
                                </span>
                                <span className="campaign-artist">
                                  {notification.campaignData.artist_name}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <div className="notification-footer">
                          <div
                            className="notification-status-badge"
                            data-status={notification?.status}
                          >
                            {notification?.notificationType === 'wallet_withdrawal_requested' && 'Payout'}
                            {notification?.notificationType !== 'wallet_withdrawal_requested' &&
                              notification?.status === 'new' && 'Submitted'}
                            {notification?.status === 'in-progress' && 'In Progress'}
                            {notification?.status === 'in-review' && 'In Review'}
                            {notification?.status === 'completed' && 'Completed'}
                          </div>
                          {notification?.campaignData?.endTime && (
                            <div className="notification-end-date">
                              <Clock size={10} />
                              {dayjs(notification.campaignData.endTime).format('MMM D, YYYY')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {adminPage < adminTotalPages && (
                  <div className="load-more-container">
                    <button className="load-more" onClick={handleLoadMore} disabled={isLoadingMore}>
                      {isLoadingMore ? 'Loading more...' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotificationBell;
